/**
 * web/ App.tsx (Issue #447 Phase A → #455 Phase H)
 *
 * Framework7 라우터 셸 + Provider 트리. `theme="auto"` + `<Views>` 단일 main view.
 *
 * Provider 순서는 app/src/App.tsx:206-216과 동일 (Issue #455 D6):
 *   <LanguageProvider>
 *     <AuthProvider>
 *       <SubscriptionProvider>
 *         <F7App> ... </F7App>
 *       </SubscriptionProvider>
 *     </AuthProvider>
 *   </LanguageProvider>
 *
 * 마운트 제외 (web 한정):
 *   - SplashScreenController / SocialLoginInitializer / ShareReceiverHandler
 *     은 모두 native 전용. web에서는 import 자체를 하지 않음 (Issue #455 G4/G5).
 *   - PaywallModal 은 web에서 mount하되 BillingAdapter web stub의 throw가 호출처 가드로 도달하지 않도록 처리.
 *
 * Phase F: PC 사이드바 — `<Panel left cover visibleBreakpoint={1024}>`.
 *   - ≥1024px: Panel이 왼쪽 고정 사이드바로 표시되고 main view가 나머지 폭 차지
 *   - <1024px: 모바일 동작 (좌측 슬라이드 오버레이)
 *   F7가 visibleBreakpoint 활성 시 `with-panel-left-cover` 클래스를 자동 부여하여
 *   메인 뷰에 좌측 패딩을 적용한다.
 *
 * `browserHistory={true}` (web 기본) — `app/` Capacitor `file://` 환경과 달리 web은
 * 브라우저 history API를 사용해야 deep link · 뒤로가기가 정상 동작.
 */

import {
  App as F7App,
  View,
  Panel,
  Page,
  Navbar,
  List,
  ListItem,
  Block,
} from 'framework7-react';
import { AuthProvider } from '@app/context/AuthContext';
import { SubscriptionProvider } from '@app/context/SubscriptionContext';
import { LanguageProvider } from '@app/contexts/LanguageProvider';
import { usePaywall } from '@app/hooks/usePaywall';
import { PaywallModal } from '@app/components/subscription/PaywallModal';
import { AuthGate } from './components/AuthGate';
import { RootErrorBoundary } from './components/RootErrorBoundary';
import { useAuthRouterSync } from './hooks/useAuthRouterSync';
import routes from './routes';

const f7params = {
  name: 'DISTILL',
  theme: 'auto' as const,
  routes,
};

const SIDEBAR_VISIBLE_BREAKPOINT = 1024;

/**
 * 단일 PaywallModal mount — usePaywall 모듈 스코프 싱글톤이 트리거.
 * 호출처는 BillingAdapter 결과(ServiceResult)로만 분기 — RC native 모듈은
 * subscription.service의 isNativePlatform 가드로 web에서는 도달하지 않음.
 */
function PaywallHost() {
  const { isPaywallOpen, closePaywall } = usePaywall();
  return <PaywallModal isOpen={isPaywallOpen} onDismiss={closePaywall} />;
}

/** Issue #484 Layer 2 — 인증 상태 변화 → F7 router 자동 sync */
function AuthRouterSync() {
  useAuthRouterSync();
  return null;
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <F7App {...f7params}>
            <RootErrorBoundary>
              {/* PC 사이드바 — visibleBreakpoint 이상에서 항상 표시 */}
              <Panel left cover visibleBreakpoint={SIDEBAR_VISIBLE_BREAKPOINT}>
                <View>
                  <Page>
                    <Navbar title="DISTILL" />
                    <Block strong inset>
                      <p>AI 콘텐츠 큐레이션</p>
                    </Block>
                    <List menuList>
                      <ListItem link="/" view=".view-main" panelClose title="홈" />
                      <ListItem link="/feed" view=".view-main" panelClose title="피드" />
                      <ListItem link="/settings" view=".view-main" panelClose title="설정" />
                      <ListItem link="/login" view=".view-main" panelClose title="로그인" />
                      <ListItem link="/privacy" view=".view-main" panelClose title="개인정보 처리방침" />
                      <ListItem link="/terms" view=".view-main" panelClose title="서비스 이용약관" />
                    </List>
                  </Page>
                </View>
              </Panel>

              <AuthRouterSync />
              <AuthGate />

              <PaywallHost />
            </RootErrorBoundary>
          </F7App>
        </SubscriptionProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}
